import { FC, MouseEvent, useCallback } from "react";
import { capitalize, IconButton, Stack } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { TypographyWithTooltip } from "@/components/common/TypographyWithTooltip";
import { setInfrastructureArchitectureRequestData } from "@/store/infrastructure-architecture/infrastructureArchitectureSlice";
import { useAppDispatch } from "@/store/hooks";
import { generateInfrastructureArchitectureThunk } from "@/store/infrastructure-architecture/thunks/generateInfrastructureArchitectureThunk";
import { CopyIconButton } from "@/components/common/CopyIconButton";
import { useHover } from "@/utils/hooks/useHover.hook";

interface InfrastructureArchitectureHierarchyItemContentProps {
  expanded: boolean;
  selected: boolean;
  resourceId?: string;
  hasChildren: boolean;
  resourceName?: string;
  resourceType?: string;
  toggleExpanded(): void;
}

export const InfrastructureArchitectureHierarchyItemContent: FC<
  InfrastructureArchitectureHierarchyItemContentProps
> = ({
  selected,
  expanded,
  resourceId,
  hasChildren,
  resourceName,
  resourceType,
  toggleExpanded,
}) => {
  const dispatch = useAppDispatch();
  const { ref, hovered } = useHover();

  const clickHandler = useCallback(() => {
    if (!resourceId || selected || !hasChildren) {
      return;
    }

    dispatch(
      setInfrastructureArchitectureRequestData({
        selectedArchitectureId: resourceId,
      }),
    );
    dispatch(generateInfrastructureArchitectureThunk());
  }, [resourceId, dispatch, selected, hasChildren]);

  const toggleClickHandler = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      e?.preventDefault();
      e?.stopPropagation();

      toggleExpanded();
    },
    [toggleExpanded],
  );

  return (
    <Stack
      ref={ref}
      direction="row"
      alignItems="center"
      spacing={1}
      onClick={clickHandler}
      ml={hasChildren ? 0 : 3.5}
      sx={{
        ...itemStackStyles,
        bgcolor: selected ? selectedItemBgColor : undefined,
        "&:hover": {
          bgcolor: hasChildren && !selected ? "action.hover" : undefined,
        },
      }}
    >
      {hasChildren && (
        <IconButton size="small" onClick={toggleClickHandler} sx={{ p: 0 }}>
          {expanded ? (
            <ExpandMoreIcon fontSize="small" />
          ) : (
            <ChevronRightIcon fontSize="small" />
          )}
        </IconButton>
      )}

      <Stack direction="row" alignItems="center" overflow="hidden">
        <Stack overflow="hidden" sx={{ opacity: hasChildren ? 1 : 0.5 }}>
          <TypographyWithTooltip
            px={0.5}
            py={0.125}
            fontSize={10}
            borderRadius={1}
            width="fit-content"
            color="text.secondary"
            bgcolor={selected ? selectedItemBgColor : "grey.200"}
            title={capitalize(resourceType ?? "")}
          />

          <TypographyWithTooltip variant="body2" title={resourceName} />
        </Stack>

        {resourceName && hovered && (
          <CopyIconButton
            sx={{ fontSize: 18, p: 0 }}
            data={resourceName}
            TooltipProps={{ placement: "top" }}
          />
        )}
      </Stack>
    </Stack>
  );
};

const selectedItemBgColor = "#3F51B514";
const itemStackStyles = {
  px: 0.5,
  py: 0.25,
  borderRadius: 1,
};
